import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Button from "../components/Button";
import { MenuIcon, SearchIcon } from "../components/icons";
import { paths } from "../routing/paths";
import logoAleph from "../assets/logo-aleph.png";

/*
 * Sticky header — Homepage reference zone 1:
 * navy surface, elevated shadow · row 1: menu / IC lockup / avatar-or-signin ·
 * row 2: centered full search bar (max 640px, never icon-only) ·
 * row 3: horizontally scrolling pill nav links.
 * Mega-menu groups (brief §7.1) arrive with the nav-data phase; until then
 * each group label links to its real archive surface.
 *
 * Compact mode (mobile media-focus brief, 2026-10): on phones (<768px) while
 * a class is loaded in the player, the header collapses to row 1 only —
 * menu · aleph mark · search button · account — so the player and source
 * text fit on one screen. The search button reveals the full search row on
 * demand; the menu drawer carries the nav links and, while compact, the
 * same search bar at its top. Both get out of the way again when playback
 * starts, and the search row also closes on a tap outside the header when
 * nothing has been typed. It doesn't flip back on pause
 * (no mid-listen layout jump). The header's live height is published as
 * the CSS variable --ic-header-h for layouts that fill the remaining screen.
 */

const PHONE_QUERY = "(max-width: 767px)";

function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => typeof window !== "undefined" && Boolean(window.matchMedia?.(query).matches));
  useEffect(() => {
    const mq = window.matchMedia?.(query);
    if (!mq) return undefined;
    const onChange = () => setMatches(mq.matches);
    onChange();
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, [query]);
  return matches;
}

export const IC_NAV_LINKS = [
  { label: "By Category", href: paths.categories() },
  { label: "By Calendar", href: paths.permalink("jewish-and-chassidic-calendar") },
  { label: "Q&A", href: paths.permalink("qa") },
  { label: "Glossary", href: paths.permalink("chassidic-glossary") },
  { label: "Live", href: paths.live() },
  { label: "Donate", href: paths.donations() },
];

const SEARCH_HINTS = ["Bitachon", "Teshuvah", "Simcha", "Emunah", "Moshiach"];

/*
 * Typewriter for the search placeholder's example term: type out, hold,
 * delete, next word — one pass through the list, then it settles back on
 * the first term and stops. Reduced motion: plain 4s swaps, same one pass.
 * Runs only while `enabled` (the homepage); elsewhere it's the static
 * first term.
 */
function useTypedHint(enabled) {
  const [hint, setHint] = useState(SEARCH_HINTS[0]);

  useEffect(() => {
    if (!enabled) {
      setHint(SEARCH_HINTS[0]);
      return undefined;
    }
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches) {
      let i = 0;
      const id = setInterval(() => {
        i += 1;
        setHint(SEARCH_HINTS[i % SEARCH_HINTS.length]);
        if (i >= SEARCH_HINTS.length) clearInterval(id);
      }, 4000);
      return () => clearInterval(id);
    }
    let word = 0;
    let len = SEARCH_HINTS[0].length;
    let deleting = false;
    let wrapped = false;
    let id;
    const tick = () => {
      let delay;
      if (deleting) {
        len -= 1;
        delay = 45;
        if (len === 0) {
          deleting = false;
          word += 1;
          if (word >= SEARCH_HINTS.length) {
            word = 0;
            wrapped = true;
          }
          delay = 400;
        }
      } else {
        len += 1;
        delay = 85;
        if (len >= SEARCH_HINTS[word].length) {
          len = SEARCH_HINTS[word].length;
          setHint(SEARCH_HINTS[word]);
          // Back on the first term after a full pass — rest here for good.
          if (wrapped) return;
          deleting = true;
          delay = 2600;
        }
      }
      setHint(SEARCH_HINTS[word].slice(0, len));
      id = setTimeout(tick, delay);
    };
    id = setTimeout(tick, 2600);
    return () => clearTimeout(id);
  }, [enabled]);

  return hint;
}

function getUser() {
  try {
    return JSON.parse(localStorage.getItem("loginData")) || null;
  } catch (_) {
    return null;
  }
}

export default function Header({ links = IC_NAV_LINKS }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [query, setQuery] = useState("");
  const hint = useTypedHint(pathname === "/");
  const user = getUser();
  const isPhone = useMediaQuery(PHONE_QUERY);
  const mediaActive = useSelector((s) => Boolean(s.player.current));
  const compact = isPhone && mediaActive;
  const [searchOpen, setSearchOpen] = useState(false);
  const headerRef = useRef(null);

  useEffect(() => {
    if (!compact) setSearchOpen(false);
  }, [compact]);

  // Back to the player: when playback starts or resumes, put the search row
  // and the menu away so the compact header is compact again.
  const playing = useSelector((s) => s.player.playing);
  useEffect(() => {
    if (playing) {
      setSearchOpen(false);
      setDrawerOpen(false);
    }
  }, [playing]);

  // A tap anywhere outside the header closes an empty search row (covers
  // going back to a player that was already playing).
  useEffect(() => {
    if (!searchOpen) return undefined;
    const onDown = (e) => {
      if (!headerRef.current?.contains(e.target) && !query.trim()) setSearchOpen(false);
    };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, [searchOpen, query]);

  // Publish the header's height for fill-the-screen layouts (ClassMedia).
  useLayoutEffect(() => {
    const el = headerRef.current;
    if (!el) return undefined;
    const publish = () => document.documentElement.style.setProperty("--ic-header-h", `${el.offsetHeight}px`);
    publish();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(publish);
    ro.observe(el);
    return () => ro.disconnect();
    // Re-publish synchronously on mode changes too (don't wait for the observer).
  }, [compact, searchOpen, drawerOpen]);

  const initials = (user?.name || "")
    .split(/\s+/)
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const submitSearch = (e) => {
    e.preventDefault();
    const q = query.trim();
    setSearchOpen(false);
    setDrawerOpen(false);
    navigate(q ? paths.search(q) : paths.search());
  };

  // One search form, rendered in the header row and (while compact) at the
  // top of the menu drawer — never both at once.
  const searchForm = ({ autoFocus, onEscape }) => (
    <form onSubmit={submitSearch} className="relative w-full max-w-[640px]">
      <span className="absolute left-[18px] top-3.5 text-[#A3A3A3] pointer-events-none">
        <SearchIcon size={20} />
      </span>
      <input
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        autoFocus={autoFocus}
        onKeyDown={(e) => {
          if (e.key === "Escape") onEscape();
        }}
        placeholder={`Search 15,000+ classes, try ${hint}`}
        aria-label="Search 15,000+ classes"
        className="bg-transparent text-ic-offwhite placeholder:text-[#A3A3A3] font-sans text-[16px] font-normal leading-6 pl-12 pr-4 h-12 w-full box-border border border-ic-lightGray rounded-3xl transition-all duration-150 ease-out focus:outline-none focus:border-ic-sky focus:shadow-ic-focus-dark-soft"
      />
    </form>
  );

  return (
    <header ref={headerRef} className="ic-scope sticky top-0 z-40 bg-ic-navy shadow-ic-elevated-dark font-sans">
      <div className={`max-w-[1280px] mx-auto px-5 flex flex-col gap-3 ${compact ? "py-2" : "py-3"}`}>
        {/* Row 1: menu · lockup · account */}
        <div className="flex items-center gap-3">
          <button
            type="button"
            aria-label="Menu"
            aria-expanded={drawerOpen}
            onClick={() => {
              setSearchOpen(false);
              setDrawerOpen((o) => !o);
            }}
            className="flex-shrink-0 w-11 h-11 bg-transparent border-0 text-ic-offwhite inline-flex items-center justify-center cursor-pointer rounded-xl transition-colors duration-150 ease-out hover:bg-white/10 focus-visible:outline-none focus-visible:shadow-ic-focus-dark"
          >
            <MenuIcon size={26} />
          </button>
          <Link to={paths.home()} className="flex items-center gap-2.5 no-underline hover:no-underline min-w-0">
            <img
              src={logoAleph}
              alt=""
              className="flex-shrink-0 w-10 h-10 rounded-full"
            />
            <span
              className={compact ? "sr-only" : "text-ic-offwhite text-[18px] font-bold leading-7 whitespace-nowrap overflow-hidden text-ellipsis"}
            >
              Inside Chassidus
            </span>
          </Link>
          <div className="flex-1" />
          {compact && (
            <button
              type="button"
              aria-label="Search"
              aria-expanded={searchOpen}
              onClick={() => {
                setDrawerOpen(false);
                setSearchOpen((o) => !o);
              }}
              className="flex-shrink-0 w-11 h-11 bg-transparent border-0 text-ic-offwhite inline-flex items-center justify-center cursor-pointer rounded-xl transition-colors duration-150 ease-out hover:bg-white/10 focus-visible:outline-none focus-visible:shadow-ic-focus-dark"
            >
              <SearchIcon size={22} />
            </button>
          )}
          {user ? (
            <Link
              to={paths.profile()}
              aria-label="Your account"
              className="flex-shrink-0 w-11 h-11 rounded-full bg-ic-navyCard border border-white/20 text-ic-offwhite text-[14px] font-bold inline-flex items-center justify-center no-underline hover:no-underline transition-colors duration-150 ease-out hover:border-ic-sky focus-visible:outline-none focus-visible:shadow-ic-focus-dark"
            >
              {initials || "IC"}
            </Link>
          ) : (
            <Button size="md" onClick={() => navigate(paths.signIn())} className="flex-shrink-0 h-11 px-6">
              Sign in
            </Button>
          )}
        </div>

        {/* Row 2: centered full search bar — never an icon, except in compact
            mode, where the search button above reveals it on demand */}
        {(!compact || searchOpen) && (
          <div className="flex justify-center">
            {searchForm({ autoFocus: compact, onEscape: () => compact && setSearchOpen(false) })}
          </div>
        )}

        {/* Row 3: scrolling pill nav (in the menu drawer while compact) */}
        {!compact && (
          <nav aria-label="Browse" className="ic-rail flex gap-2 overflow-x-auto pb-1">
            {links.map((l) => (
              <Link
                key={l.label}
                to={l.href}
                className="flex-shrink-0 text-[16px] font-semibold leading-6 text-ic-offwhite no-underline px-4 py-2.5 rounded-3xl transition-all duration-150 ease-out hover:text-ic-sky hover:bg-white/5 hover:no-underline focus-visible:outline-none focus-visible:shadow-ic-focus-dark"
              >
                {l.label}
              </Link>
            ))}
          </nav>
        )}
      </div>

      {/* Simple drawer until the mega-menu lands */}
      {drawerOpen && (
        <div className="bg-ic-navy border-t border-white/10 px-5 py-4">
          {/* Compact header hides the search row — offer it first in the menu (no autofocus: opening the menu shouldn't pop the keyboard) */}
          {compact && (
            <div className="max-w-[1280px] mx-auto pb-3">{searchForm({ autoFocus: false, onEscape: () => setDrawerOpen(false) })}</div>
          )}
          <ul className="m-0 p-0 list-none flex flex-col gap-1 max-w-[1280px] mx-auto">
            {links.map((l) => (
              <li key={l.label}>
                <Link
                  to={l.href}
                  onClick={() => setDrawerOpen(false)}
                  className="block text-[18px] font-semibold leading-7 text-ic-offwhite no-underline py-2.5 hover:text-ic-sky hover:no-underline"
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </header>
  );
}
